import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import logo1 from "../../imgs/logo 2.webp";
import logoHome from "../../imgs/logo 3.webp";
import "./Header.css";
import {
  UilEstate,
  UilBriefcaseAlt,
  UilShoppingBag,
  UilGlobe,
  UilMegaphone,
  UilFastMail,
  UilMultiply,
  UilAngleDown,
} from "@iconscout/react-unicons";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "@uidotdev/usehooks";
import i18next from "i18next";
import { useStoreCart } from "../../pages/store/StoreCartContext";
import StoreProfileMenu from "../../pages/store/components/StoreProfileMenu";
import { StorePriceDisplay } from "../../pages/store/components/StorePriceDisplay";
import axios from "axios";
import { resolveUrl } from "../../utils/storeUtils";

const Header = () => {
  const [showMobile, setShowMobile] = useState(false);
  const [showOrdersSubmenu, setShowOrdersSubmenu] = useState(false);
  const [showProjectsMenu, setShowProjectsMenu] = useState(false);
  const projectsMenuRef = useRef(null);
  const [t, i18n] = useTranslation("global");
  const [lang, setLang] = useLocalStorage("lang", "en");
  const dir = i18next.dir(i18n.language);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isStore = location.pathname.startsWith("/store");
  const isHome = location.pathname === "/";
  const storeQueryParam = searchParams.get("q") || "";
  const [storeSearch, setStoreSearch] = useState(storeQueryParam);
  const [storeSearchFocused, setStoreSearchFocused] = useState(false);
  const storeSearchWrapRef = useRef(null);
  const [storeProductsIndex, setStoreProductsIndex] = useState([]);
  const [storeProductsLoaded, setStoreProductsLoaded] = useState(false);
  const [homeScrolled, setHomeScrolled] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);
  const lastScrollY = useRef(0);
  const storeHeaderRef = useRef(null);
  const [homeSearchQuery, setHomeSearchQuery] = useState("");
  const { cartTotal, cartCount, wishlistCount } = useStoreCart();

  const showMobileMenu = () => setShowMobile(true);
  const closeMobileMenu = () => {
    setShowMobile(false);
    setShowOrdersSubmenu(false);
  };

  // Lock body scroll when mobile menu is open (mobile-only).
  // On desktop, forcing body overflow can break `position: sticky`.
  useEffect(() => {
    const isMobileViewport = () => (window?.innerWidth || 0) < 920;

    if (showMobile && isMobileViewport()) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [showMobile]);

  // Close mobile menu on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    if (showMobile) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showMobile]);

  function handleChange(e) {
    setLang(e.target.value);
    handleChangeLanguage(e.target.value);
  }

  useEffect(() => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language || lang;
    document.body.dir = dir;
  }, [dir, i18n, lang]);

  useEffect(() => {
    if (!isStore) return;
    setStoreSearch(storeQueryParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStore, storeQueryParam]);

  // Load store products once for typeahead search.
  useEffect(() => {
    if (!isStore || storeProductsLoaded) return;
    let cancelled = false;
    axios
      .get("/store/products")
      .then(({ data }) => {
        if (cancelled) return;
        setStoreProductsIndex(Array.isArray(data) ? data : []);
        setStoreProductsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setStoreProductsIndex([]);
        setStoreProductsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isStore, storeProductsLoaded]);

  // Close suggestions on outside click.
  useEffect(() => {
    if (!isStore) return;
    const onDoc = (e) => {
      const el = storeSearchWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setStoreSearchFocused(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [isStore]);

  const storeSearchQuery = (storeSearch || "").trim();
  const apiBaseUrl = axios.defaults.baseURL || "";
  const storeSuggestions = useMemo(() => {
    if (!storeSearchQuery) return [];
    const q = storeSearchQuery.toLowerCase();
    const scored = storeProductsIndex
      .map((p) => {
        const name = String(p?.name || "");
        const arName = String(p?.arName || "");
        const cat = String(p?.category || "");
        const hay = `${name} ${arName} ${cat}`.toLowerCase();
        const idx = hay.indexOf(q);
        return { p, idx };
      })
      .filter((x) => x.idx >= 0)
      .sort((a, b) => a.idx - b.idx)
      .slice(0, 5)
      .map((x) => x.p);
    return scored;
  }, [storeProductsIndex, storeSearchQuery]);

  const showStoreSuggestions = isStore && storeSearchFocused && storeSearchQuery.length > 0;

  const suggestionImageUrl = useCallback(
    (product) => {
      if (!product) return "";
      const first =
        (Array.isArray(product.images) && product.images.length > 0
          ? product.images[0]?.url
          : null) || product?.image?.url || "";
      return first ? resolveUrl(first, apiBaseUrl) : "";
    },
    [apiBaseUrl]
  );


  useEffect(() => {
    setScrollHidden(false);
    lastScrollY.current = typeof window !== "undefined" ? window.scrollY : 0;
  }, [location.pathname]);

  /** Store home hero: `calc(100dvh - offset)` in Store.css — must match real header height, not a guess. */
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!isStore) {
      document.documentElement.style.removeProperty("--store-header-offset");
      return undefined;
    }
    const el = storeHeaderRef.current;
    if (!el) return undefined;

    const apply = () => {
      requestAnimationFrame(() => {
        const h = Math.ceil(el.getBoundingClientRect().height);
        if (h > 0) {
          document.documentElement.style.setProperty("--store-header-offset", `${h}px`);
        }
      });
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      document.documentElement.style.removeProperty("--store-header-offset");
    };
  }, [isStore, scrollHidden, showMobile, i18n.language, location.pathname]);

  useEffect(() => {
    if (showMobile) {
      setScrollHidden(false);
      return undefined;
    }

    const onScroll = () => {
      const y = window.scrollY || 0;

      if (isHome) {
        setHomeScrolled(y > 40);
      }

      if (y < 36) {
        setScrollHidden(false);
      } else {
        const delta = y - lastScrollY.current;
        if (delta > 12) setScrollHidden(true);
        else if (delta < -12) setScrollHidden(false);
      }
      lastScrollY.current = y;
    };

    lastScrollY.current = window.scrollY || 0;
    if (!isHome) {
      setHomeScrolled(false);
    } else {
      setHomeScrolled(lastScrollY.current > 40);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome, showMobile]);

  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    const handleClickOrHoverOutside = (event) => {
      if (
        projectsMenuRef.current &&
        !projectsMenuRef.current.contains(event.target) &&
        !document.querySelector(".header").contains(event.target)
      ) {
        setShowProjectsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOrHoverOutside);
    document.addEventListener("mousemove", handleClickOrHoverOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOrHoverOutside);
      document.removeEventListener("mousemove", handleClickOrHoverOutside);
    };
  }, []);

  const handleLinkClick = (path) => {
    setShowProjectsMenu(false);
    navigate(path);
  };

  const storeMenuItems = useMemo(
    () => [
      {
        to: "/store",
        end: true,
        label: t("header.storeNavHome", { defaultValue: "Store home" }),
        icon: <UilEstate color="currentColor" size={22} />,
      },
      {
        to: "/store/wishlist",
        end: false,
        label: t("header.storeNavWishlist", { defaultValue: "Wishlist" }),
        icon: <i className="fa-regular fa-heart" aria-hidden="true" />,
      },
      {
        to: "/store/checkout",
        end: false,
        label: t("header.storeNavCheckout", { defaultValue: "Checkout" }),
        icon: <i className="fa-solid fa-cart-shopping" aria-hidden="true" />,
      },
    ],
    [t]
  );

  const storeOrdersRouteActive = location.pathname.startsWith("/store/my-orders");

  const storeSearchDebounceRef = useRef(null);

  const buildNextStoreParams = useCallback(
    (next) => {
      const q = (next || "").trim();
      const nextParams = new URLSearchParams(searchParams);
      if (q) nextParams.set("q", q);
      else nextParams.delete("q");
      return nextParams;
    },
    [searchParams]
  );

  const applyStoreSearch = useCallback(
    (next, { immediate = false } = {}) => {
      if (!isStore) return;

      const nextParams = buildNextStoreParams(next);
      const q = (next || "").trim();

      // If the user is on store home, route them to the results page so searching always "works".
      const isStoreHome = location.pathname === "/store" || location.pathname === "/store/";
      if (isStoreHome) {
        const target = q
          ? `/store/category/all?${nextParams.toString()}`
          : "/store";
        navigate(target, { replace: true });
        return;
      }

      // Otherwise keep the current store page, only update query params.
      if (immediate) {
        setSearchParams(nextParams, { replace: true });
        return;
      }

      if (storeSearchDebounceRef.current) {
        clearTimeout(storeSearchDebounceRef.current);
      }
      storeSearchDebounceRef.current = setTimeout(() => {
        setSearchParams(nextParams, { replace: true });
      }, 250);
    },
    [isStore, buildNextStoreParams, location.pathname, navigate, setSearchParams]
  );

  useEffect(() => {
    return () => {
      if (storeSearchDebounceRef.current) {
        clearTimeout(storeSearchDebounceRef.current);
        storeSearchDebounceRef.current = null;
      }
    };
  }, []);

  const handleHomeSearch = (e) => {
    e.preventDefault();
    const q = homeSearchQuery.trim();
    if (q) navigate(`/projects?search=${encodeURIComponent(q)}`);
    else navigate("/projects");
  };

  const projectsChevronColor =
    isHome && !homeScrolled
      ? "rgba(255, 255, 255, 0.92)"
      : isHome && homeScrolled
        ? "rgba(255, 255, 255, 0.9)"
        : "#1A1A1A";

  return (
    <header
      ref={isStore ? storeHeaderRef : undefined}
      className={`header${isStore ? " header--store" : ""}${isHome ? " header--home" : ""}${
        isHome && homeScrolled ? " header--home-scrolled" : ""
      }${scrollHidden ? " header--scroll-hidden" : ""}`}
    >
      <div className="container">
        {isStore ? (
          <NavLink
            to="/"
            end
            className="logo logo--store-back"
            data-aos={"fade-down"}
            data-aos-duration="1200"
            aria-label={t("header.storeLogoHome", {
              defaultValue: "Alramlaa home",
            })}
          >
            <img src={logo1} alt="" />
          </NavLink>
        ) : (
          <NavLink
            to="/"
            end
            className="logo"
            data-aos={"fade-down"}
            data-aos-duration="1200"
          >
            <img src={isHome && !isStore ? logoHome : logo1} alt="Logo" />
          </NavLink>
        )}

        {!isStore && (
          <ul className="links">
            <li data-aos={"fade-down"} data-aos-duration="600">
              <NavLink to="/">{t("header.home")}</NavLink>
            </li>
            <li
              className="project"
              ref={projectsMenuRef}
              data-aos={"fade-down"}
              data-aos-duration="800"
              onMouseEnter={() => setShowProjectsMenu(true)}
              onMouseLeave={() => setShowProjectsMenu(false)}
            >
              <Link to={"/projects"} className="main-btn">
                {t("header.projects")}
              </Link>
              <UilAngleDown size={27} color={projectsChevronColor} />
            </li>
            <li data-aos={"fade-down"} data-aos-duration="1200">
              <NavLink to="/store">{t("header.store", { defaultValue: "Store" })}</NavLink>
            </li>
            <li data-aos={"fade-down"} data-aos-duration="1300">
              <NavLink to="about">{t("header.about")}</NavLink>
            </li>
            <li data-aos={"fade-down"} data-aos-duration="1400">
              <NavLink to="/join">{t("header.joinus")}</NavLink>
            </li>
            <li data-aos={"fade-down"} data-aos-duration="1600">
              <NavLink to="/contact">{t("header.contact")}</NavLink>
            </li>
          </ul>
        )}

        <div className="max-header" data-aos={"fade-down"} data-aos-duration="1200">
          {isStore ? (
            <>
              <div
                ref={storeSearchWrapRef}
                className="header-search-wrap store-header-search store-header-search-desktop header-search-wrap--with-results"
                role="search"
              >
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                <input
                  type="search"
                  placeholder={t("header.storeSearchPlaceholder", {
                    defaultValue: "Search for products",
                  })}
                  value={storeSearch}
                  onChange={(e) => {
                    const next = e.target.value;
                    setStoreSearch(next);
                    applyStoreSearch(next);
                  }}
                  onFocus={() => setStoreSearchFocused(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyStoreSearch(storeSearch, { immediate: true });
                      setStoreSearchFocused(false);
                    } else if (e.key === "Escape") {
                      setStoreSearch("");
                      applyStoreSearch("", { immediate: true });
                      setStoreSearchFocused(false);
                    }
                  }}
                  aria-label={t("header.storeSearchAria", {
                    defaultValue: "Search for products",
                  })}
                />
                {storeSearch?.trim() ? (
                  <button
                    type="button"
                    className="header-search-clear"
                    onClick={() => {
                      setStoreSearch("");
                      applyStoreSearch("", { immediate: true });
                    }}
                    aria-label={t("header.storeSearchClear", { defaultValue: "Clear search" })}
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                ) : null}

                {showStoreSuggestions ? (
                  <div className="header-search-results" role="listbox" aria-label="Search results">
                    {storeSuggestions.length === 0 ? (
                      <div className="header-search-empty">
                        {t("header.storeSearchNoResults", { defaultValue: "No results" })}
                      </div>
                    ) : (
                      <ul className="header-search-results-list">
                        {storeSuggestions.map((p) => (
                          <li key={p._id}>
                            <button
                              type="button"
                              className="header-search-result-row"
                              onClick={() => {
                                setStoreSearchFocused(false);
                                navigate(`/store/product/${p._id}`);
                              }}
                            >
                              <span className="header-search-result-thumb" aria-hidden="true">
                                {suggestionImageUrl(p) ? (
                                  <img src={suggestionImageUrl(p)} alt="" loading="lazy" />
                                ) : (
                                  <i className="fa-regular fa-image" aria-hidden="true" />
                                )}
                              </span>
                              <span className="header-search-result-name">
                                {((i18n.language || "").toLowerCase().startsWith("ar")
                                  ? p?.arName || p?.name
                                  : p?.name || p?.arName) || ""}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      className="header-search-seeall"
                      onClick={() => {
                        setStoreSearchFocused(false);
                        applyStoreSearch(storeSearch, { immediate: true });
                        navigate(
                          storeSearchQuery
                            ? `/store/category/all?q=${encodeURIComponent(storeSearchQuery)}`
                            : "/store/category/all",
                          { replace: true }
                        );
                      }}
                    >
                      {t("header.storeSearchSeeAll", { defaultValue: "See all" })}
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="store-header-actions">
                <button
                  type="button"
                  className="header-icon-btn header-wishlist-btn"
                  onClick={() => navigate("/store/wishlist")}
                  aria-label={t("header.wishlistWithCount", {
                    count: wishlistCount,
                    defaultValue: `Wishlist (${wishlistCount})`,
                  })}
                >
                  <i className="fa-regular fa-heart" aria-hidden="true" />
                  {wishlistCount > 0 && (
                    <span className="header-wishlist-count" aria-hidden="true">
                      {wishlistCount}
                    </span>
                  )}
                </button>

                <StoreProfileMenu buttonClassName="header-icon-btn" />

                <button
                  type="button"
                  className="header-cart-btn"
                  onClick={() => navigate("/store/checkout")}
                  aria-label={t("header.cartWithCount", {
                    count: cartCount,
                    defaultValue: `Cart (${cartCount} items)`,
                  })}
                >
                  <span className="header-cart-total">
                    <StorePriceDisplay amount={cartTotal} />
                  </span>
                  <span className="header-cart-count">{cartCount}</span>
                  <i className="fa-solid fa-cart-shopping" aria-hidden="true" />
                </button>

                <div className="lang">
                  <select
                    value={lang}
                    onChange={handleChange}
                    aria-label={t("header.selectLanguage", { defaultValue: "Select language" })}
                  >
                    <option value="ar">العربية</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <button
                  type="button"
                  className={`header-hamburger${showMobile ? " is-open" : ""}`}
                  onClick={() => (showMobile ? closeMobileMenu() : showMobileMenu())}
                  aria-label={
                    showMobile
                      ? t("header.closeMenu", { defaultValue: "Close menu" })
                      : t("header.openMenu", { defaultValue: "Open menu" })
                  }
                  aria-expanded={showMobile}
                >
                  <span className="header-hamburger-inner" aria-hidden="true">
                    <span className="header-hamburger-line" />
                    <span className="header-hamburger-line" />
                    <span className="header-hamburger-line" />
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              {isHome && (
                <form
                  className="header-home-search"
                  onSubmit={handleHomeSearch}
                  role="search"
                  aria-label={t("hero.searchAria", { defaultValue: "Search projects" })}
                >
                  <input
                    type="search"
                    placeholder={t("hero.input")}
                    value={homeSearchQuery}
                    onChange={(e) => setHomeSearchQuery(e.target.value)}
                    aria-label={t("hero.searchAria", { defaultValue: "Search projects" })}
                  />
                  <button
                    type="submit"
                    aria-label={t("hero.searchSubmit", { defaultValue: "Search" })}
                  >
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                  </button>
                </form>
              )}
              <div className="lang">
                <select value={lang} onChange={handleChange}>
                  <option value="ar">العربية</option>
                  <option value="en">English</option>
                </select>
              </div>
              <button
                className="showMenuMobile"
                type="button"
                onClick={showMobileMenu}
                aria-label={t("header.openMenu", { defaultValue: "Open menu" })}
              >
                <i className="uil uil-bars" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {isStore && (
          <div
            className="header-search-wrap store-header-search store-search-mobile"
            role="search"
            data-aos={"fade-down"}
            data-aos-duration="1200"
          >
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              type="search"
              placeholder={t("header.storeSearchPlaceholder", {
                defaultValue: "Search for products",
              })}
              value={storeSearch}
              onChange={(e) => {
                const next = e.target.value;
                setStoreSearch(next);
                applyStoreSearch(next);
              }}
              onFocus={() => setStoreSearchFocused(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyStoreSearch(storeSearch, { immediate: true });
                  setStoreSearchFocused(false);
                } else if (e.key === "Escape") {
                  setStoreSearch("");
                  applyStoreSearch("", { immediate: true });
                  setStoreSearchFocused(false);
                }
              }}
              aria-label={t("header.storeSearchAria", {
                defaultValue: "Search for products",
              })}
            />
            {storeSearch?.trim() ? (
              <button
                type="button"
                className="header-search-clear"
                onClick={() => {
                  setStoreSearch("");
                  applyStoreSearch("", { immediate: true });
                  setStoreSearchFocused(false);
                }}
                aria-label={t("header.storeSearchClear", { defaultValue: "Clear search" })}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            ) : null}

            {showStoreSuggestions ? (
              <div className="header-search-results header-search-results--mobile" role="listbox" aria-label="Search results">
                {storeSuggestions.length === 0 ? (
                  <div className="header-search-empty">
                    {t("header.storeSearchNoResults", { defaultValue: "No results" })}
                  </div>
                ) : (
                  <ul className="header-search-results-list">
                    {storeSuggestions.map((p) => (
                      <li key={p._id}>
                        <button
                          type="button"
                          className="header-search-result-row"
                          onClick={() => {
                            setStoreSearchFocused(false);
                            navigate(`/store/product/${p._id}`);
                          }}
                        >
                          <span className="header-search-result-thumb" aria-hidden="true">
                            {suggestionImageUrl(p) ? (
                              <img src={suggestionImageUrl(p)} alt="" loading="lazy" />
                            ) : (
                              <i className="fa-regular fa-image" aria-hidden="true" />
                            )}
                          </span>
                          <span className="header-search-result-name">
                            {((i18n.language || "").toLowerCase().startsWith("ar")
                              ? p?.arName || p?.name
                              : p?.name || p?.arName) || ""}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  className="header-search-seeall"
                  onClick={() => {
                    setStoreSearchFocused(false);
                    applyStoreSearch(storeSearch, { immediate: true });
                    navigate(
                      storeSearchQuery
                        ? `/store/category/all?q=${encodeURIComponent(storeSearchQuery)}`
                        : "/store/category/all",
                      { replace: true }
                    );
                  }}
                >
                  {t("header.storeSearchSeeAll", { defaultValue: "See all" })}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {showMobile && (
          <div
            className="mobile-backdrop"
            onClick={closeMobileMenu}
            onKeyDown={(e) => e.key === "Enter" && closeMobileMenu()}
            role="button"
            tabIndex={0}
            aria-label={t("header.closeMenu", { defaultValue: "Close menu" })}
          />
        )}

        <div className={`mobile ${showMobile ? "open" : ""}`} role="dialog" aria-modal="true" aria-label="Main menu">
          <div className="mobile-header">
            {isStore ? (
              <span className="mobile-title">
                {t("header.storeMenuTitle", { defaultValue: "Menu" })}
              </span>
            ) : (
              <NavLink
                to="/"
                end
                className="mobile-logo-link"
                onClick={closeMobileMenu}
                aria-label={t("header.home")}
              >
                <img
                  src={logoHome}
                  alt="Alramlaa"
                  className="mobile-logo-img"
                  width={160}
                  height={48}
                />
              </NavLink>
            )}
            <button
              type="button"
              className="mobile-close"
              onClick={closeMobileMenu}
              aria-label={t("header.closeMenu", { defaultValue: "Close menu" })}
            >
              <UilMultiply color={"#fff"} size={24} />
            </button>
          </div>

          <ul className="mobile-list">
            {isStore ? (
              <>
                {storeMenuItems.map((item) => (
                  <li key={item.to} className="mobile-nav-li">
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={closeMobileMenu}
                      className={({ isActive }) =>
                        `mobile-nav-item${isActive ? " is-active" : ""}`
                      }
                    >
                      <span className="mobile-nav-icon-wrap" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span className="mobile-nav-text">{item.label}</span>
                    </NavLink>
                  </li>
                ))}

                <li className="mobile-menu-orders-wrap">
                  <button
                    type="button"
                    className={`mobile-menu-orders-btn ${showOrdersSubmenu ? "is-open" : ""}${
                      storeOrdersRouteActive ? " is-active-route" : ""
                    }`}
                    onClick={() => setShowOrdersSubmenu((v) => !v)}
                    aria-expanded={showOrdersSubmenu}
                    aria-controls="mobile-orders-submenu"
                  >
                    <i className="fa-solid fa-box-open" aria-hidden="true" />
                    <span>{t("header.storeNavOrders", { defaultValue: "My orders" })}</span>
                    <UilAngleDown size={20} color="rgba(255,255,255,0.9)" className="mobile-menu-orders-chevron" />
                  </button>
                  <ul id="mobile-orders-submenu" className={`mobile-orders-submenu ${showOrdersSubmenu ? "open" : ""}`}>
                    <li onClick={closeMobileMenu}>
                      <Link to="/store/my-orders">
                        {t("header.storeNavOrders", { defaultValue: "My orders" })}
                      </Link>
                    </li>
                    <li onClick={closeMobileMenu}>
                      <Link to="/store/my-orders">
                        {t("header.storeNavTrackOrder", { defaultValue: "Track order" })}
                      </Link>
                    </li>
                    <li onClick={closeMobileMenu}>
                      <Link to="/store/my-orders">
                        {t("header.storeNavReturns", { defaultValue: "Return & exchange" })}
                      </Link>
                    </li>
                  </ul>
                </li>

                <li className="mobile-menu-lang" onClick={(e) => e.stopPropagation()}>
                  <span className="mobile-menu-lang-label">
                    {t("header.storeLanguage", { defaultValue: "Language" })}
                  </span>
                  <div className="mobile-lang-select-wrap">
                    <select
                      className="mobile-lang-select"
                      value={lang}
                      onChange={(e) => {
                        handleChange(e);
                        closeMobileMenu();
                      }}
                      aria-label={t("header.selectLanguage", { defaultValue: "Select language" })}
                    >
                      <option value="ar">العربية</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </li>
              </>
            ) : (
              <>
                <li className="mobile-nav-li">
                  <NavLink
                    to="/"
                    end
                    onClick={closeMobileMenu}
                    className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
                  >
                    <span className="mobile-nav-icon-wrap" aria-hidden="true">
                      <UilEstate color="currentColor" size={22} />
                    </span>
                    <span className="mobile-nav-text">{t("header.home")}</span>
                  </NavLink>
                </li>
                <li className="mobile-nav-li">
                  <NavLink
                    to="/projects"
                    onClick={closeMobileMenu}
                    className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
                  >
                    <span className="mobile-nav-icon-wrap" aria-hidden="true">
                      <UilBriefcaseAlt color="currentColor" size={22} />
                    </span>
                    <span className="mobile-nav-text">{t("header.projects")}</span>
                  </NavLink>
                </li>
                <li className="mobile-nav-li">
                  <NavLink
                    to="/store"
                    onClick={closeMobileMenu}
                    className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
                  >
                    <span className="mobile-nav-icon-wrap" aria-hidden="true">
                      <UilShoppingBag color="currentColor" size={22} />
                    </span>
                    <span className="mobile-nav-text">{t("header.store", { defaultValue: "Store" })}</span>
                  </NavLink>
                </li>
                <li className="mobile-nav-li">
                  <NavLink
                    to="/about"
                    onClick={closeMobileMenu}
                    className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
                  >
                    <span className="mobile-nav-icon-wrap" aria-hidden="true">
                      <UilGlobe color="currentColor" size={22} />
                    </span>
                    <span className="mobile-nav-text">{t("header.about")}</span>
                  </NavLink>
                </li>
                <li className="mobile-nav-li">
                  <NavLink
                    to="/join"
                    onClick={closeMobileMenu}
                    className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
                  >
                    <span className="mobile-nav-icon-wrap" aria-hidden="true">
                      <UilMegaphone color="currentColor" size={22} />
                    </span>
                    <span className="mobile-nav-text">{t("header.joinus")}</span>
                  </NavLink>
                </li>
                <li className="mobile-nav-li">
                  <NavLink
                    to="/contact"
                    onClick={closeMobileMenu}
                    className={({ isActive }) => `mobile-nav-item${isActive ? " is-active" : ""}`}
                  >
                    <span className="mobile-nav-icon-wrap" aria-hidden="true">
                      <UilFastMail color="currentColor" size={22} />
                    </span>
                    <span className="mobile-nav-text">{t("header.contact")}</span>
                  </NavLink>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      {!isStore && (
        <div
          className={`projects-links ${showProjectsMenu ? "open" : ""} ${
            lang === "ar" ? "ar" : "en"
          }`}
          onMouseEnter={() => setShowProjectsMenu(true)}
          onMouseLeave={() => setShowProjectsMenu(false)}
        >
          <button onClick={() => handleLinkClick(`/projects/${t("header.hospitality")}`)}>
            {t("header.hospitality")}
          </button>
          <hr />
          <button onClick={() => handleLinkClick(`/projects/${t("header.commercial")}`)}>
            {t("header.commercial")}
          </button>
          <hr />
          <button onClick={() => handleLinkClick(`/projects/${t("header.entertainment")}`)}>
            {t("header.entertainment")}
          </button>
          <hr />
          <button onClick={() => handleLinkClick(`/projects/${t("header.residential")}`)}>
            {t("header.residential")}
          </button>
          <hr />
          <button onClick={() => handleLinkClick(`/projects/${t("header.education")}`)}>
            {t("header.education")}
          </button>
          <hr />
          <button onClick={() => handleLinkClick(`/projects/${t("header.workspace")}`)}>
            {t("header.workspace")}
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
